import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { loadStripe } from "@stripe/stripe-js";
import CouponInput from '../AdminDashboard/CouponInput';
import {
  FaStar,
  FaCheckCircle,
  FaArrowRight,
  FaLock,
  FaFileAlt,
  FaClock,
  FaChartBar,
  FaArrowLeft
} from "react-icons/fa";

const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLIC_KEY);

const MockTest = () => {
  const [firstname, setFirstName] = useState("");
  const [mockTestAccess, setMockTestAccess] = useState(false);
  const [loading, setLoading] = useState(true);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [studentId, setStudentId] = useState(null);
  const [testHistory, setTestHistory] = useState([]);
  const navigate = useNavigate();

  const mockPrice = "$450";

  useEffect(() => {
    const fetchData = async () => {
      const userData = localStorage.getItem("user");
      if (userData) {
        const user = JSON.parse(userData);
        setStudentId(user.id);
        setFirstName(user.firstName || user.username.split(" ")[0]);

        try {
          const mockTestResponse = await fetch(
            `${process.env.REACT_APP_BACKEND_URL}/api/mock-test/mocktest-package-access/${user.id}`
          );
          const mockTestData = await mockTestResponse.json();
          setMockTestAccess(mockTestData.hasAccess);
        } catch (error) {
          console.error("Error fetching data:", error);
        } finally {
          setLoading(false);
        }
      }
    };

    fetchData();
  }, []);

  const handleMockTestPayment = async (e) => {
    e.preventDefault();
    const stripe = await stripePromise;

    const endpoint = appliedCoupon
      ? "/api/payment/create-mocktest-package-session-with-coupon"
      : "/api/payment/create-mocktest-package-session";

    const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}${endpoint}`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        student_id: studentId,
        coupon_code: appliedCoupon?.id
      }),
    });

    if (response.ok) {
      const session = await response.json();
      const result = await stripe.redirectToCheckout({ sessionId: session.id });
      if (result.error) alert(result.error.message);
    } else {
      alert("Error creating payment session");
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-purple-100 py-8 px-4">
      <div className="max-w-6xl mx-auto">
        {/* Back Button */}
        <button
          onClick={() => navigate('/new-student-dashboard')}
          className="flex items-center text-gray-600 hover:text-gray-800 mb-6"
        >
          <FaArrowLeft className="mr-2" />
          Back to Dashboard
        </button>

        <div className="text-center mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-2">
            Mock Test Center
          </h1>
          <p className="text-lg text-gray-600">Choose your test option</p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Mock Test Package Card - Same as before */}
          <div className="group relative p-[2px] rounded-xl bg-gradient-to-br from-yellow-400 to-orange-500 hover:from-yellow-300 hover:to-orange-400 transition-all duration-300 shadow-lg hover:shadow-xl">
             <div className="absolute top-1 -right-10 z-10">
              <div className="bg-red-500 text-white px-4 py-1.5 rounded-full text-sm font-bold shadow-lg transform rotate-[30deg] border-2 border-white">
                COMING SOON
              </div>
            </div>
            <div className="bg-white rounded-xl overflow-hidden h-full">
              <div className="p-6 lg:p-8 h-full flex flex-col">
                <div className="flex flex-col items-center text-center mb-6">
                  <div className="bg-yellow-100 p-4 rounded-full group-hover:bg-yellow-50 transition mb-4">
                    <FaStar className="text-yellow-500 text-3xl group-hover:text-yellow-600 transition" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 group-hover:text-gray-900 transition">
                    Mock Test Package
                  </h2>
                  <span className="bg-yellow-100 text-yellow-800 px-3 py-1 rounded-full text-sm font-medium group-hover:bg-yellow-50 group-hover:text-yellow-700 transition mt-2">
                    {mockPrice}/package
                  </span>
                </div>

                <p className="text-gray-600 mb-6">
                  10 Complete Mock Tests with Creative Writing, Reading, Mathematical Reasoning and Thinking Skills under test conditions.
                </p>

                <ul className="space-y-2 mb-6">
                  {["10 Complete Mock Tests", "4 subjects in each test", "Level-wise unlocking system", "Progress-based learning"].map((item, i) => (
                    <li key={i} className="flex items-start">
                      <FaCheckCircle className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>

                {/* Coupon Input Section */}
                {!mockTestAccess && (
                  <div className="mt-4">
                    <CouponInput
                      onCouponApplied={setAppliedCoupon}
                      onCouponRemoved={() => setAppliedCoupon(null)}
                      type="mocktest-package"
                    />
                  </div>
                )}

                <div className="mt-6 sm:mt-8 flex flex-col sm:flex-row justify-between items-center space-y-3 sm:space-y-0">
                  {mockTestAccess ? (
                    <span className="px-3 py-1 sm:px-4 sm:py-2 rounded-lg text-sm sm:text-base bg-green-200 text-green-800 group-hover:bg-green-300 group-hover:text-green-700 transition w-full sm:w-auto text-center">
                      Active
                    </span>
                  ) : (
                    <button
                      disabled
                      className="px-4 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-medium rounded-lg opacity-70 cursor-not-allowed flex items-center justify-center space-x-2"
                      // onClick={handleMockTestPayment}
                      // className="px-4 py-2 sm:px-6 sm:py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white font-medium rounded-lg hover:opacity-90 transition hover:shadow-md w-full sm:w-auto text-sm sm:text-base flex items-center justify-center space-x-2"
                    >
                      <FaLock className="text-sm" />
                      {/* <span>Buy Now</span> */}
                      <span>Coming Soon</span>
                    </button>
                  )}
                  <button
                    onClick={() => navigate('/mock-tests')}
                    className="text-blue-600 hover:text-blue-800 font-medium flex items-center group text-sm sm:text-base"
                  >
                    <span className="group-hover:underline">
                      {mockTestAccess ? 'View Tests' : 'Explore Package'}
                    </span>
                    <FaArrowRight className="ml-2 group-hover:translate-x-1 transition-transform" />
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* New: Mock Test Individual Card */}
          <div className="group relative p-[2px] rounded-xl bg-gradient-to-br from-teal-400 to-blue-500 hover:from-teal-300 hover:to-blue-400 transition-all duration-300 shadow-lg hover:shadow-xl">
            <div className="bg-white rounded-xl overflow-hidden h-full">
              <div className="p-6 lg:p-8 h-full flex flex-col">
                <div className="flex flex-col items-center text-center mb-6">
                  <div className="bg-teal-100 p-4 rounded-full group-hover:bg-teal-50 transition mb-4">
                    <FaFileAlt className="text-teal-500 text-3xl group-hover:text-teal-600 transition" />
                  </div>
                  <h2 className="text-2xl font-bold text-gray-800 group-hover:text-gray-900 transition">
                    Individual Mock Tests
                  </h2>
                  <span className="bg-teal-100 text-teal-800 px-3 py-1 rounded-full text-sm font-medium group-hover:bg-teal-50 group-hover:text-teal-700 transition mt-2">
                    $95/test
                  </span>
                </div>

                <p className="text-gray-600 mb-6">
                  Unlock individual subjects and features
                </p>

                <ul className="space-y-2 mb-6">
                  {["Subjects Wise Mock Test", "Mathematical Reasoning", "Thinking Skills", "Creative Writing", "Reading"].map((item, i) => (
                    <li key={i} className="flex items-start">
                      <FaCheckCircle className="text-green-500 mt-1 mr-2 flex-shrink-0" />
                      <span className="text-gray-700">{item}</span>
                    </li>
                  ))}
                </ul>

                <div className="mt-auto pt-6">
                  <button
                    onClick={() => navigate('/individual-mock-tests')}
                    className="px-6 py-3 bg-gradient-to-r from-teal-500 to-blue-500 text-white font-medium rounded-lg hover:opacity-90 transition hover:shadow-md w-full"
                  >
                    View Available Tests
                  </button>
                  <p className="text-center text-gray-500 text-sm mt-2">
                    Purchase tests individually as needed
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MockTest;